/**
 * Who may manage whom — UI mirror of the API gate
 * (ibook-backend/src/services/permissionAggregation.service.js → canManageUser).
 * The server is authoritative; this only decides which row actions to disable.
 *
 * Seniority is the existing `roles.roleId` (superAdmin 1, admin 2, manager 3,
 * accountant 4, salesperson 5, employee 6, member 7) — LOWER is more senior.
 * No role table is redefined here; roleId travels on the data already.
 */

/** Admin-level access sits just below a real Admin (roleId 2). */
export const FULL_ACCESS_LEVEL = 2.5;
const UNRANKED_LEVEL = Number.MAX_SAFE_INTEGER;

export interface ManageCheckParty {
    userId?: string;
    /** roles.roleId of the ASSIGNED role. Never changed by the overlay. */
    roleId?: number | null;
    hasFullAccess?: boolean;
    isAccountOwner?: boolean;
}

export const seniorityLevel = (roleId?: number | null, hasFullAccess = false): number => {
    const fromRole = typeof roleId === 'number' && isFinite(roleId) ? roleId : UNRANKED_LEVEL;
    return hasFullAccess ? Math.min(fromRole, FULL_ACCESS_LEVEL) : fromRole;
};

/**
 * Can `actor` edit `target`'s role / permissions? Same rules as the server: the
 * owner may manage anyone and may not be managed, nobody edits themselves, and
 * otherwise the actor must be strictly more senior.
 */
export const canManageUser = (actor: ManageCheckParty, target: ManageCheckParty): boolean => {
    if (actor.isAccountOwner) return true;
    if (actor.userId && target.userId && String(actor.userId) === String(target.userId)) return false;
    if (target.isAccountOwner) return false;

    return seniorityLevel(actor.roleId, actor.hasFullAccess)
        < seniorityLevel(target.roleId, target.hasFullAccess);
};
